import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Tag } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { API } from '../App';

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await API.get('/blog');
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#2C5530]" data-testid="blog-hero">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-white/70 text-sm uppercase tracking-[0.2em] mb-4">Insights</p>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-6xl text-white font-medium mb-6">
              Mental Golf Blog
            </h1>
            <p className="text-white/80 text-xl leading-relaxed">
              Practical tips, strategies, and insights to strengthen your mental game and perform under pressure.
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-24 md:py-32" data-testid="blog-posts">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          {loading ? (
            <div className="text-center py-20">
              <div className="w-8 h-8 border-2 border-[#2C5530] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-[#737373]">Loading posts...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[#737373] text-lg">No blog posts yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <article
                  key={post.id}
                  className="bg-white border border-[#E7E5E4] overflow-hidden group"
                  data-testid={`blog-post-${index}`}
                >
                  {post.cover_image && (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={post.cover_image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6 md:p-8">
                    <div className="flex items-center gap-4 mb-4">
                      <span className="flex items-center gap-2 text-[#737373] text-sm">
                        <Calendar className="w-4 h-4" />
                        {formatDate(post.created_at)}
                      </span>
                    </div>
                    <h2 className="font-['Playfair_Display'] text-xl md:text-2xl text-[#1A1A1A] mb-3 group-hover:text-[#2C5530] transition-colors">
                      <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                    </h2>
                    <p className="text-[#57534E] leading-relaxed mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.tags.slice(0, 3).map((tag, i) => (
                          <span
                            key={i}
                            className="flex items-center gap-1 text-xs bg-[#F2F0E9] text-[#57534E] px-2 py-1"
                          >
                            <Tag className="w-3 h-3" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <Link
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center gap-2 text-[#2C5530] font-semibold text-sm uppercase tracking-wider hover:gap-3 transition-all"
                      data-testid={`read-more-${index}`}
                    >
                      Read More
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;
